<i18n>
{
	"en": {
		"searchForArea": "Search for areas",
		"defineNewArea": "Define new area",
		"cancel": "Cancel",
		"failedToFetch": "Failed to fetch areas",
		"failedToDelete": "Failed to delete area"
	},
	"no": {
		"searchForArea": "Søk etter områder",
		"defineNewArea": "Definer nytt område",
		"cancel": "Avbryt",
		"failedToFetch": "Kunne ikke hente områder",
		"failedToDelete": "Kunne ikke slette område"
	}
}
</i18n>
<template>
	<t-dialog
		:icon="icon"
		:title="title"
		:isShown="isShown"
		iconType="bp"
		:hasBody="false"
		@ok="onDialogOk"
		@cancel="onCancel"
		@close="onDialogClose"
		@shown="onShown"
	>
		<div class="locations-dialog">
			<div class="dialog-map-container">
				<locations-dialog-map
					v-if="dialogReady"
					ref="map"
					:mapAreas="mapAreas"
					:terminals="terminals"
				/>
			</div>

			<LocationsDialogGrid
				v-if="dialogReady"
				:locations="locations"
				:checkedIds="checkedIds"
				@editArea="editArea"
				@expandArea="expandArea"
				@changeOp="changeOp"
				@deleteArea="toggleRemoveArea($event, false)"
				@onRowsSelected="onRowsSelected"
			/>
		</div>

		<template slot="additional-buttons">
			<t-button
				appearance="minimal"
				iconBefore="draw"
				style="margin-left:auto;"
				@click="addArea"
			>
				{{ $t('defineNewArea') }}
			</t-button>
		</template>
		<ConfirmRemoveArea
			:isShown="confirmRemoveAreaVisible"
			:item="areaToDelete"
			@close="toggleRemoveArea(null, false)"
			@confirm="confirmRemoveArea"
		/>
	</t-dialog>
</template>

<script>
import LocationsDialogMap from './LocationsDialog/LocationsDialogMap';
import LocationsDialogGrid from './LocationsDialog/LocationsDialogGrid';
import { mapActions, mapState } from './store/locations.store';
import ConfirmRemoveArea from './ConfirmRemoveArea';

export default {
	components: {
		LocationsDialogMap,
		ConfirmRemoveArea,
		LocationsDialogGrid
	},
	props: {
		isShown: {
			required: true,
			type: Boolean,
			default:false
		},
		icon: String,
		dialogType: {
			required: true,
			type: String,
			default: null
		},
		existingFilters: Array,
		title: String
	},
	data() {
		return {
			locations: [],
			checkedIds: [],
			dialogReady: null,
			expandTimeout: {},
			confirmRemoveAreaVisible: false,
			areaToDelete: null,
			deleteNew: false
		};
	},
	computed: {
		...mapState(['terminals']),
		mapAreas() {
			return this.locations.filter(x => this.checkedIds.indexOf(x.id) > -1);
		},
		nonPolygonKeys() {
			let keys = [];
			this.existingFilters.forEach(x => {
				if (x.type !== 'polygon-filter')
					keys.push(x.id);
			});
			return keys;
		}
	},
	async mounted() {
		await this.getTerminals();
	},
	methods: {
		...mapActions([
			'fetchFilterlessLocations',
			'setLocations',
			'expandLocation',
			'deleteLocation',
			'fetchSingleLocation',
			'expandLatLong',
			'setFromDialog',
			'getTerminals'
		]),
		async onShown() {
			this.dialogReady = true;
			this.checkedIds = this.existingFilters.map( x => x.id );
			await this.updateFilteredItems();
			await this.$nextTick();
		},
		async updateFilteredItems() {
			try {
				this.locations = await this.fetchFilterlessLocations();
				for (let filter of this.existingFilters) {
					let index = this.locations.findIndex(x => x.id === filter.id);
					if (index > -1)
						this.locations[index] = filter;
					else
						this.locations.push(filter);
				}
			} catch (e) {
				this.$notify({
					type: 'error',
					title: this.$t('failedToFetch')
				});
			}
		},
		onRowsSelected(keys) {
			this.checkedIds = [...this.nonPolygonKeys, ...keys];
		},
		onDialogOk() {
			this.$emit('selected', this.mapAreas);
		},
		onCancel() {
			this.$emit('selected', this.existingFilters);
		},
		onDialogClose() {
			this.dialogReady = false;
			this.$emit('close');
		},
		addArea() {
			this.setFromDialog(this.dialogType);
			this.$router.push({
				name: 'locationsMap',
			});
		},
		editArea(area) {
			this.setFromDialog(this.dialogType);
			if (area.type === 'polygon-filter') {
				this.$router.push({
					name: 'savedLocationsMap',
					params: { areaId: area.id },
				});
			}
		},
		expandArea(location) {
			//if (!location.latitude && !location.longitude || !location.area) return;
			if (location.radiusValue === 0) {
				location.expanded = null;
			} else if (location.area) {
				clearTimeout(this.expandTimeout[location.id]);
				this.expandTimeout[location.id] = setTimeout(() => {
					this.expand(location);
				}, 800);
			} else if (location.latitude && location.longitude) {
				clearTimeout(this.expandTimeout[location.id]);
				this.expandTimeout[location.id] = setTimeout(() => {
					this.expandPoint(location);
				}, 800);
			} else {
				return;
			}
		},
		async expand(locationData) {
			let expanded = JSON.parse(await this.expandLocation({ geometry: locationData.area.geometry, radius: locationData.radiusValue }));

			let location = this.locations.find( filter => filter.id === locationData.id );
			if (location)
				this.$set(location, 'expanded', expanded);
		},
		async expandPoint(locationData) {
			let locationGeometry = `{ "type": "Point", "coordinates": ['${locationData.longitude}' ,'${locationData.latitude}'] }`;
			let expanded = JSON.parse(await this.expandLatLong({ geometry: locationGeometry, radius: locationData.radiusValue }));
			let location = this.locations.find( filter => filter.id === locationData.id );
			if (location) {
				this.$set(location, 'expanded', expanded);
			}
		},
		changeOp(locationData) {
			let location = this.locations.find( filter => filter.id === locationData.id );
			if (location) {
				if (location.op === 'NOT')
					this.$set(location, 'op', 'OR');
				else
					this.$set(location, 'op', 'NOT');
			}
		},

		toggleRemoveArea(area,newFilter) {
			this.areaToDelete = area;
			this.deleteNew = newFilter;
			if (this.areaToDelete) this.confirmRemoveAreaVisible = true;
			else this.confirmRemoveAreaVisible = false;
		},
		confirmRemoveArea() {
			try {
				if (this.areaToDelete) this.deleteLocation(this.areaToDelete.id);
				this.locations = this.locations.filter( filter => filter.id !== this.areaToDelete.id );
			} catch (e) {
				this.$notify({
					type: 'error',
					title: this.$t('failedToDelete')
				});
			}
		}
	}
};
</script>

<style lang="scss" scoped>
.locations-dialog{
	display: grid;
	flex-direction: column;
	height:100%;
	grid-template-rows: 250px 500px;
}

.dialog-searchResults-container {
	height: 36px;
	vertical-align: middle;
}
.dialog-map-container{
	display: flex;
	width: 680px;
}
</style>
